import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Title,
  Text,
  Grid,
  Card,
  Metric,
  BarList,
  BarChart,
  ProgressBar,
  Badge,
} from '@tremor/react';
import { KPICard } from '@/components/KPICard';
import {
  getImpugnacoes,
  getImpugnaCasos,
  getImpugnaCasoDetalhe,
  extractErrorMessage,
} from '@/lib/api';

// ── Tipos (payload de GET /impugnacoes?days=N) ──────────────────────────────

interface KpiGraduacao {
  atual: number | null;
  meta: number;
  progresso: number | null;
  status: 'ok' | 'pendente' | 'sem_dados';
}

interface KpiDescarte extends KpiGraduacao {
  n_aceita: number;
  n_rejeitada: number;
}

interface KpiThumb extends KpiGraduacao {
  n_up: number;
  n_total: number;
}

interface ReguaGraduacao {
  impugnacoes_concluidas: KpiGraduacao;
  taxa_descarte: KpiDescarte;
  taxa_thumb_positivo: KpiThumb;
  pronto_para_graduar: boolean;
}

interface FunilPonto {
  dia: string;
  total: number;
  [status: string]: number | string;
}

interface FunilUso {
  serie: FunilPonto[];
  status_labels: string[];
}

interface Detector {
  tipo: string;
  disparos: number;
  aceitas: number;
  rejeitadas: number;
  sugeridas: number;
  taxa_aceitacao: number | null;
  confianca_media: number | null;
}

interface AdocaoOrg {
  org_id: number;
  org_name: string | null;
  n: number;
  ultima: string | null;
}

interface Adocao {
  orgs_distintas: number;
  top10: AdocaoOrg[];
  orgs_ativas_plataforma: number;
  pct_experimentou: number | null;
}

interface FeedbackSemana {
  semana: string;
  up: number;
  down: number;
}

interface Comentario {
  data: string;
  org_id: number | null;
  thumb: string;
  texto: string;
  impugnacao_id: number | null;
}

interface QualidadeFeedback {
  serie_semanal: FeedbackSemana[];
  comentarios: Comentario[];
}

interface Saude {
  total: number;
  n_erros: number;
  pct_erro: number | null;
  pct_erro_parse_gate: number | null;
  n_erro_parse_gate: number;
  n_erro_outros: number;
  regeneracoes_media_por_impugnacao: number | null;
  regeneracoes_nota: string;
}

interface UltimaLinha {
  id: number;
  org_id: number | null;
  org_name: string | null;
  lado_cliente: string;
  status: string;
  divergencias_detectadas: number;
  divergencias_aplicadas: number;
  thumb: string | null;
  created_at: string;
}

interface ImpugnacoesData {
  disponivel: boolean;
  erro?: string;
  days: number;
  hoje: string;
  regua_graduacao: ReguaGraduacao;
  funil_uso: FunilUso;
  detectores: Detector[];
  adocao: Adocao;
  qualidade_feedback: QualidadeFeedback;
  saude: Saude;
  ultimas_25: UltimaLinha[];
}

// ── Helpers ─────────────────────────────────────────────────────────────────

const PERIODOS = [7, 30, 90];

const STATUS_COLORS: Record<string, string> = {
  COMPLETED: 'emerald',
  ERROR: 'red',
  AGUARDANDO_REVISAO: 'amber',
  GERANDO: 'blue',
  PENDING: 'slate',
};

function fmtInt(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  return new Intl.NumberFormat('pt-BR').format(n);
}

function fmtPct(n: number | null | undefined, decimals = 1): string {
  if (n === null || n === undefined) return '—';
  return `${(n * 100).toFixed(decimals)}%`;
}

function fmtDia(iso: string): string {
  const [, m, d] = iso.split('-');
  return `${d}/${m}`;
}

function fmtDataHora(iso: string): string {
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return iso;
  return dt.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function corProgresso(status: string): 'emerald' | 'amber' | 'slate' {
  if (status === 'ok') return 'emerald';
  if (status === 'pendente') return 'amber';
  return 'slate';
}

function fmtBRL(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

const SEV_COR: Record<string, string> = {
  alta: 'bg-red-900/40 text-red-300 border border-red-700/40',
  media: 'bg-amber-900/30 text-amber-300 border border-amber-700/40',
  baixa: 'bg-slate-800 text-slate-400 border border-slate-700',
};

const STATUS_COR: Record<string, 'emerald' | 'red' | 'amber' | 'slate'> = {
  COMPLETED: 'emerald',
  ERROR: 'red',
  GERANDO: 'amber',
  PENDING: 'amber',
  AGUARDANDO_REVISAO: 'slate',
};

interface QualFlag { tipo: string; sev: string; msg: string; }
interface CasoQual {
  n_linhas: number; n_base_nula: number; n_valor_nulo: number;
  soma_verbas: number | null; pct_mismatch: number | null; flags: QualFlag[];
}
interface CasoRow {
  id: number; org_id: number; org_name: string | null; numero_processo: string | null;
  lado_cliente: string | null; status: string; created_at: string | null;
  total_conta_original: number | null; delta_total: number | null;
  tem_peticao: boolean; tem_comparativo: boolean; criador_email: string | null;
  divergencias_detectadas: number; divergencias_aplicadas: number;
  thumb: string | null; qualidade: CasoQual;
}

/** Drill-in por-caso das impugnações (lista clicável + detalhe). A aba Cases
 * mostra só cálculos; isto expõe as impugnações e seus flags de qualidade. */
function ImpugnaCasos() {
  const [sel, setSel] = useState<number | null>(null);
  const listaQ = useQuery({
    queryKey: ['impugna-casos'],
    queryFn: async () => (await getImpugnaCasos()).data,
  });
  const detQ = useQuery({
    queryKey: ['impugna-caso', sel],
    queryFn: async () => (await getImpugnaCasoDetalhe(sel as number)).data,
    enabled: sel !== null,
  });

  const casos: CasoRow[] = listaQ.data?.casos ?? [];
  const det = detQ.data?.caso;

  return (
    <Card>
      <div className="flex items-center justify-between">
        <Title>Casos de impugnação</Title>
        <Text className="text-xs text-slate-400">
          {casos.length} impugnação(ões) real(is) — clique para auditar
        </Text>
      </div>
      <Text className="text-xs text-slate-500">
        A aba <span className="font-mono">Cases</span> só lista cálculos. Aqui
        ficam as impugnações, com flags de qualidade (entrega vazia, base nula,
        soma que não fecha, lado do cliente).
      </Text>

      {listaQ.isLoading && <Text className="mt-3">carregando…</Text>}
      {listaQ.isError && (
        <div className="mt-3 text-sm text-red-400" role="alert">
          {extractErrorMessage(listaQ.error, 'erro ao carregar casos')}
        </div>
      )}
      {!listaQ.isLoading && listaQ.data && listaQ.data.disponivel === false && (
        <div className="mt-3 text-sm text-amber-400">
          indisponível: {listaQ.data.erro}
        </div>
      )}

      {casos.length > 0 && (
        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="text-xs uppercase text-slate-500">
              <tr>
                <th className="py-2 pr-4">Processo</th>
                <th className="py-2 pr-4">Org</th>
                <th className="py-2 pr-4">Lado</th>
                <th className="py-2 pr-4">Status</th>
                <th className="py-2 pr-4">Conta</th>
                <th className="py-2 pr-4">Div.</th>
                <th className="py-2 pr-4">Qualidade</th>
                <th className="py-2 pr-4">Data</th>
              </tr>
            </thead>
            <tbody>
              {casos.map((c) => (
                <tr
                  key={c.id}
                  onClick={() => setSel(c.id === sel ? null : c.id)}
                  className={`cursor-pointer border-t border-slate-800 hover:bg-slate-800/40 ${
                    sel === c.id ? 'bg-blue-900/20' : ''
                  }`}
                >
                  <td className="py-2 pr-4 font-mono text-xs">
                    {c.numero_processo ?? `#${c.id}`}
                  </td>
                  <td className="py-2 pr-4 text-xs">{c.org_name ?? c.org_id}</td>
                  <td className="py-2 pr-4 text-xs">{c.lado_cliente ?? '—'}</td>
                  <td className="py-2 pr-4">
                    <Badge color={STATUS_COR[c.status] ?? 'slate'} size="xs">
                      {c.status}
                    </Badge>
                  </td>
                  <td className="py-2 pr-4 text-xs">{fmtBRL(c.total_conta_original)}</td>
                  <td className="py-2 pr-4">
                    {c.divergencias_aplicadas}/{c.divergencias_detectadas}
                  </td>
                  <td className="py-2 pr-4">
                    <div className="flex flex-wrap gap-1">
                      {c.qualidade.flags.length === 0 && (
                        <span className="text-xs text-emerald-400">ok</span>
                      )}
                      {c.qualidade.flags.map((f, i) => (
                        <span
                          key={i}
                          title={f.msg}
                          className={`rounded px-1.5 py-0.5 text-[10px] ${SEV_COR[f.sev] ?? SEV_COR.baixa}`}
                        >
                          {f.tipo}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="py-2 pr-4 text-xs">{fmtDia(c.created_at ?? '')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Detalhe da impugnação selecionada */}
      {sel !== null && (
        <div className="mt-5 rounded-lg border border-slate-700 bg-slate-900/40 p-4">
          {detQ.isLoading && <Text>carregando detalhe…</Text>}
          {det && (
            <div className="space-y-4">
              <div className="flex items-start justify-between">
                <div>
                  <Title className="text-base">
                    {det.numero_processo ?? `Impugnação #${det.id}`}
                  </Title>
                  <Text className="text-xs text-slate-400">
                    {det.org_name ?? det.org_id} · {det.criador_email ?? '—'} ·
                    lado {det.lado_cliente ?? '—'} · conta {det.conta_tipo ?? '—'}
                  </Text>
                </div>
                <button
                  type="button"
                  onClick={() => setSel(null)}
                  className="text-xs text-slate-500 hover:text-slate-300"
                >
                  fechar ✕
                </button>
              </div>

              {/* Flags de qualidade */}
              {det.qualidade?.flags?.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {det.qualidade.flags.map((f: QualFlag, i: number) => (
                    <span key={i} className={`rounded px-2 py-1 text-xs ${SEV_COR[f.sev] ?? SEV_COR.baixa}`}>
                      {f.msg}
                    </span>
                  ))}
                </div>
              )}

              {/* Resumo + totais */}
              <Grid numItems={1} numItemsMd={3} className="gap-3">
                <Card className="bg-slate-900/60">
                  <Text className="text-xs">Total da conta original</Text>
                  <Metric className="text-lg">{fmtBRL(det.total_conta_original)}</Metric>
                </Card>
                <Card className="bg-slate-900/60">
                  <Text className="text-xs">Total retificado</Text>
                  <Metric className="text-lg">{fmtBRL(det.total_conta_retificada)}</Metric>
                </Card>
                <Card className="bg-slate-900/60">
                  <Text className="text-xs">Delta (a favor do cliente)</Text>
                  <Metric className="text-lg">{fmtBRL(det.delta_total)}</Metric>
                </Card>
              </Grid>

              {det.resumo_analise && (
                <div className="rounded border border-slate-700 bg-slate-950/40 p-3">
                  <Text className="text-xs font-semibold text-slate-300">Resumo entregue ao cliente</Text>
                  <Text className="mt-1 text-xs text-slate-400 whitespace-pre-wrap">
                    {det.resumo_analise}
                  </Text>
                </div>
              )}

              <div className="flex gap-3 text-xs">
                <span className={det.tem_peticao ? 'text-emerald-400' : 'text-slate-500'}>
                  {det.tem_peticao ? '✓ petição gerada' : '✗ sem petição'}
                </span>
                <span className={det.tem_comparativo ? 'text-emerald-400' : 'text-slate-500'}>
                  {det.tem_comparativo ? '✓ comparativo gerado' : '✗ sem comparativo'}
                </span>
                {det.thumb && <span>feedback: {det.thumb === 'up' ? '👍' : '👎'}</span>}
              </div>

              {/* Divergências */}
              <div>
                <Text className="text-xs font-semibold text-slate-300">
                  Divergências detectadas ({det.divergencias?.length ?? 0})
                </Text>
                {(det.divergencias?.length ?? 0) === 0 ? (
                  <Text className="mt-1 text-xs text-amber-400">
                    Nenhuma divergência — nada a impugnar foi encontrado.
                  </Text>
                ) : (
                  <div className="mt-2 overflow-x-auto">
                    <table className="w-full text-left text-xs">
                      <thead className="text-[10px] uppercase text-slate-500">
                        <tr>
                          <th className="py-1 pr-3">Tipo</th>
                          <th className="py-1 pr-3">Verba</th>
                          <th className="py-1 pr-3">Conta</th>
                          <th className="py-1 pr-3">Correto</th>
                          <th className="py-1 pr-3">Delta</th>
                          <th className="py-1 pr-3">Conf.</th>
                          <th className="py-1 pr-3">Fundamento</th>
                        </tr>
                      </thead>
                      <tbody>
                        {det.divergencias.map((d: any) => (
                          <tr key={d.id} className="border-t border-slate-800 align-top">
                            <td className="py-1 pr-3">{d.tipo}</td>
                            <td className="py-1 pr-3">{d.verba_chave ?? '—'}</td>
                            <td className="py-1 pr-3">{fmtBRL(d.valor_conta)}</td>
                            <td className="py-1 pr-3">{fmtBRL(d.valor_correto)}</td>
                            <td className="py-1 pr-3">{fmtBRL(d.delta)}</td>
                            <td className="py-1 pr-3">
                              {d.confianca != null ? `${Math.round(d.confianca * 100)}%` : '—'}
                            </td>
                            <td className="py-1 pr-3 max-w-md text-slate-400">
                              {d.fundamento ?? d.descricao ?? '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Linhas da conta */}
              <details>
                <summary className="cursor-pointer text-xs font-semibold text-slate-300">
                  Linhas da conta ({det.linhas?.length ?? 0}) — extração por-verba
                </summary>
                <div className="mt-2 overflow-x-auto">
                  <table className="w-full text-left text-xs">
                    <thead className="text-[10px] uppercase text-slate-500">
                      <tr>
                        <th className="py-1 pr-3">Verba</th>
                        <th className="py-1 pr-3">Base</th>
                        <th className="py-1 pr-3">Índice</th>
                        <th className="py-1 pr-3">Valor</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(det.linhas ?? []).map((l: any, i: number) => (
                        <tr key={i} className="border-t border-slate-800">
                          <td className="py-1 pr-3">{l.verba_label ?? l.verba_chave ?? '—'}</td>
                          <td className={`py-1 pr-3 ${l.base_calculo == null ? 'text-amber-400' : ''}`}>
                            {l.base_calculo == null ? 'nula' : fmtBRL(l.base_calculo)}
                          </td>
                          <td className="py-1 pr-3">{l.indice_correcao ?? '—'}</td>
                          <td className={`py-1 pr-3 ${l.valor == null ? 'text-amber-400' : ''}`}>
                            {l.valor == null ? 'nulo' : fmtBRL(l.valor)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

export function ImpugnacoesPage() {
  const [days, setDays] = useState(30);

  const { data, isLoading, error } = useQuery<ImpugnacoesData>({
    queryKey: ['impugnacoes', days],
    queryFn: async () => (await getImpugnacoes(days)).data,
    refetchInterval: 60_000,
  });

  const errMsg = error
    ? extractErrorMessage(error, 'erro ao carregar impugnações')
    : data && !data.disponivel
      ? (data.erro ?? 'dados indisponíveis')
      : null;

  const regua = data?.regua_graduacao;
  const funil = data?.funil_uso;
  const detectores = data?.detectores ?? [];
  const adocao = data?.adocao;
  const qf = data?.qualidade_feedback;
  const saude = data?.saude;
  const ultimas = data?.ultimas_25 ?? [];

  const funilChart = useMemo(() => {
    const labels = funil?.status_labels ?? [];
    return (funil?.serie ?? []).map((p) => {
      const ponto: Record<string, number | string> = { dia: fmtDia(p.dia) };
      for (const s of labels) ponto[s] = (p[s] as number) ?? 0;
      return ponto;
    });
  }, [funil]);

  const detectorChart = detectores.map((d) => ({
    tipo: d.tipo,
    Aceitas: d.aceitas,
    Rejeitadas: d.rejeitadas,
    Sugeridas: d.sugeridas,
  }));

  const top10Items = (adocao?.top10 ?? []).map((o) => ({
    name: o.org_name ?? `Org ${o.org_id}`,
    value: o.n,
  }));

  const feedbackChart = (qf?.serie_semanal ?? []).map((s) => ({
    semana: fmtDia(s.semana),
    'Positivo (up)': s.up,
    'Negativo (down)': s.down,
  }));

  return (
    <div className="space-y-6">
      <header className="space-y-1">
        <div className="flex items-center justify-between">
          <Title>Impugnações</Title>
          <div className="flex items-center gap-1 rounded-md border border-slate-700 p-0.5">
            {PERIODOS.map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setDays(p)}
                className={`rounded px-2.5 py-1 text-xs transition ${
                  days === p
                    ? 'bg-blue-600/30 text-blue-200'
                    : 'text-slate-400 hover:bg-slate-800 hover:text-slate-200'
                }`}
              >
                {p}d
              </button>
            ))}
          </div>
        </div>
        <Text>
          Monitor do beta do subproduto Impugna (impugnação diferencial de
          cálculos). Orgs internas/teste excluídas de tudo. O funil de uso é
          recortado pelo período; a régua de graduação e adoção são
          cumulativas desde a abertura do beta.
        </Text>
      </header>

      {/* Drill-in por-caso (Diego 2026-07-07): ver e auditar cada impugnação */}
      <ImpugnaCasos />

      {/* Hero: régua de graduação do beta */}
      <Card className="border border-blue-700/40 bg-blue-900/10">
        <div className="flex items-center justify-between">
          <Title className="text-blue-100">
            Critérios para graduar o beta e ativar cobrança
          </Title>
          {regua && (
            <Badge color={regua.pronto_para_graduar ? 'emerald' : 'amber'} size="lg">
              {regua.pronto_para_graduar ? 'pronto para graduar' : 'ainda não'}
            </Badge>
          )}
        </div>
        <Text className="text-xs text-slate-400">
          Decisão: começar a cobrar 2 créditos por impugnação quando os 3
          critérios abaixo forem atingidos simultaneamente.
        </Text>
        {isLoading && <Text className="mt-3">carregando…</Text>}
        {!isLoading && errMsg && (
          <div className="mt-3 text-sm text-red-400" role="alert">
            {errMsg}
          </div>
        )}
        {!isLoading && !errMsg && regua && (
          <Grid numItems={1} numItemsMd={3} className="mt-4 gap-4">
            <Card className="bg-slate-900/40">
              <Text>Impugnações reais concluídas</Text>
              <Metric>{fmtInt(regua.impugnacoes_concluidas.atual)}</Metric>
              <Text className="text-xs text-slate-400">
                meta: {regua.impugnacoes_concluidas.meta}
              </Text>
              <ProgressBar
                value={(regua.impugnacoes_concluidas.progresso ?? 0) * 100}
                color={corProgresso(regua.impugnacoes_concluidas.status)}
                className="mt-2"
              />
            </Card>
            <Card className="bg-slate-900/40">
              <Text>Taxa de descarte de divergências</Text>
              <Metric>{fmtPct(regua.taxa_descarte.atual)}</Metric>
              <Text className="text-xs text-slate-400">
                meta: &lt;{fmtPct(regua.taxa_descarte.meta, 0)} · rejeitadas{' '}
                {regua.taxa_descarte.n_rejeitada} / aceitas+rejeitadas{' '}
                {regua.taxa_descarte.n_aceita + regua.taxa_descarte.n_rejeitada}
              </Text>
              <ProgressBar
                value={(regua.taxa_descarte.progresso ?? 0) * 100}
                color={corProgresso(regua.taxa_descarte.status)}
                className="mt-2"
              />
            </Card>
            <Card className="bg-slate-900/40">
              <Text>Thumbs positivos</Text>
              <Metric>{fmtPct(regua.taxa_thumb_positivo.atual)}</Metric>
              <Text className="text-xs text-slate-400">
                meta: ≥{fmtPct(regua.taxa_thumb_positivo.meta, 0)} ·{' '}
                {regua.taxa_thumb_positivo.n_up} / {regua.taxa_thumb_positivo.n_total}
              </Text>
              <ProgressBar
                value={(regua.taxa_thumb_positivo.progresso ?? 0) * 100}
                color={corProgresso(regua.taxa_thumb_positivo.status)}
                className="mt-2"
              />
            </Card>
          </Grid>
        )}
      </Card>

      {/* Funil de uso */}
      <Card>
        <Title>Funil de uso</Title>
        <Text className="text-xs text-slate-400">
          Impugnações criadas por dia, decompostas por status atual.
        </Text>
        {isLoading && <Text>carregando…</Text>}
        {!isLoading && errMsg && (
          <div className="text-sm text-red-400" role="alert">
            {errMsg}
          </div>
        )}
        {!isLoading && !errMsg && (
          <BarChart
            className="mt-3 h-64"
            data={funilChart}
            index="dia"
            categories={funil?.status_labels ?? []}
            colors={['emerald', 'red', 'amber', 'blue', 'slate']}
            stack
            valueFormatter={fmtInt}
          />
        )}
      </Card>

      {/* Detectores + Adoção */}
      <Grid numItems={1} numItemsLg={2} className="gap-4">
        <Card>
          <Title>Detectores</Title>
          <Text className="text-xs text-slate-400">
            Disparos por tipo (radar de falso-positivo em produção).
          </Text>
          {isLoading && <Text>carregando…</Text>}
          {!isLoading && errMsg && (
            <div className="text-sm text-red-400" role="alert">
              {errMsg}
            </div>
          )}
          {!isLoading && !errMsg && (
            <>
              <BarChart
                className="mt-3 h-56"
                data={detectorChart}
                index="tipo"
                categories={['Aceitas', 'Rejeitadas', 'Sugeridas']}
                colors={['emerald', 'red', 'slate']}
                stack
                valueFormatter={fmtInt}
              />
              <div className="mt-3 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-800 text-left text-xs uppercase tracking-wide text-slate-400">
                      <th className="py-2 pr-4 font-medium">Tipo</th>
                      <th className="py-2 pr-4 font-medium">Disparos</th>
                      <th className="py-2 pr-4 font-medium">Taxa aceitação</th>
                      <th className="py-2 pr-4 font-medium">Confiança média</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detectores.map((d) => (
                      <tr key={d.tipo} className="border-b border-slate-800/50 text-slate-200">
                        <td className="py-2 pr-4 font-mono text-xs">{d.tipo}</td>
                        <td className="py-2 pr-4">{d.disparos}</td>
                        <td className="py-2 pr-4">{fmtPct(d.taxa_aceitacao)}</td>
                        <td className="py-2 pr-4">{fmtPct(d.confianca_media)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </Card>

        <Card>
          <Title>Adoção</Title>
          <Text className="text-xs text-slate-400">
            Orgs distintas que criaram ≥1 impugnação real.
          </Text>
          {isLoading && <Text>carregando…</Text>}
          {!isLoading && errMsg && (
            <div className="text-sm text-red-400" role="alert">
              {errMsg}
            </div>
          )}
          {!isLoading && !errMsg && adocao && (
            <>
              <div className="mt-2 flex items-center gap-6">
                <div>
                  <Metric>{fmtInt(adocao.orgs_distintas)}</Metric>
                  <Text className="text-xs text-slate-400">orgs que experimentaram</Text>
                </div>
                <div>
                  <Metric>{fmtPct(adocao.pct_experimentou)}</Metric>
                  <Text className="text-xs text-slate-400">
                    de {fmtInt(adocao.orgs_ativas_plataforma)} orgs ativas da plataforma
                  </Text>
                </div>
              </div>
              <Text className="mb-1 mt-4 text-xs uppercase tracking-wide text-slate-400">
                top 10 por volume
              </Text>
              {top10Items.length > 0 ? (
                <BarList data={top10Items} valueFormatter={fmtInt} color="blue" />
              ) : (
                <Text className="text-sm text-slate-400">nenhuma org real ainda</Text>
              )}
            </>
          )}
        </Card>
      </Grid>

      {/* Qualidade / feedback */}
      <Grid numItems={1} numItemsLg={2} className="gap-4">
        <Card>
          <Title>Feedback (thumbs) por semana</Title>
          {isLoading && <Text>carregando…</Text>}
          {!isLoading && errMsg && (
            <div className="text-sm text-red-400" role="alert">
              {errMsg}
            </div>
          )}
          {!isLoading && !errMsg && (
            <BarChart
              className="mt-3 h-56"
              data={feedbackChart}
              index="semana"
              categories={['Positivo (up)', 'Negativo (down)']}
              colors={['emerald', 'red']}
              valueFormatter={fmtInt}
            />
          )}
        </Card>

        <Card>
          <Title>Comentários</Title>
          <Text className="text-xs text-slate-400">
            Ouro no beta — texto livre dado junto com o thumb.
          </Text>
          {isLoading && <Text>carregando…</Text>}
          {!isLoading && errMsg && (
            <div className="text-sm text-red-400" role="alert">
              {errMsg}
            </div>
          )}
          {!isLoading && !errMsg && (
            <div className="mt-3 max-h-80 space-y-2 overflow-y-auto">
              {(qf?.comentarios ?? []).length === 0 ? (
                <Text className="text-sm text-slate-400">nenhum comentário ainda</Text>
              ) : (
                qf!.comentarios.map((c, i) => (
                  <div
                    key={`${c.impugnacao_id}-${i}`}
                    className="rounded border border-slate-800 p-2 text-sm"
                  >
                    <div className="flex items-center justify-between">
                      <Badge color={c.thumb === 'up' ? 'emerald' : 'red'} size="xs">
                        {c.thumb === 'up' ? '👍' : '👎'}
                      </Badge>
                      <span className="text-xs text-slate-400">
                        org {c.org_id ?? '—'} · impugnação #{c.impugnacao_id ?? '—'} ·{' '}
                        {fmtDataHora(c.data)}
                      </span>
                    </div>
                    <Text className="mt-1 text-slate-200">{c.texto}</Text>
                  </div>
                ))
              )}
            </div>
          )}
        </Card>
      </Grid>

      {/* Saúde */}
      <Grid numItems={1} numItemsMd={3} className="gap-4">
        <KPICard
          label="% ERROR (do total)"
          value={isLoading ? null : fmtPct(saude?.pct_erro)}
          loading={isLoading}
          error={errMsg}
          hint={`${fmtInt(saude?.n_erros)} de ${fmtInt(saude?.total)} impugnações`}
        />
        <KPICard
          label="ERROR por parse-gate"
          value={isLoading ? null : fmtPct(saude?.pct_erro_parse_gate)}
          loading={isLoading}
          error={errMsg}
          hint={`${fmtInt(saude?.n_erro_parse_gate)} parse-gate vs ${fmtInt(saude?.n_erro_outros)} outros motivos`}
        />
        <KPICard
          label="Regenerações / impugnação"
          value={isLoading ? null : (saude?.regeneracoes_media_por_impugnacao?.toFixed(1) ?? '—')}
          loading={isLoading}
          error={errMsg}
          hint={saude?.regeneracoes_nota}
        />
      </Grid>

      {/* Tabela das últimas 25 */}
      <Card>
        <Title>Últimas 25 impugnações</Title>
        {isLoading && <Text>carregando…</Text>}
        {!isLoading && errMsg && (
          <div className="text-sm text-red-400" role="alert">
            {errMsg}
          </div>
        )}
        {!isLoading && !errMsg && (
          <div className="mt-3 overflow-x-auto">
            {ultimas.length === 0 ? (
              <Text className="text-sm text-slate-400">nenhuma impugnação real ainda</Text>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-800 text-left text-xs uppercase tracking-wide text-slate-400">
                    <th className="py-2 pr-4 font-medium">ID</th>
                    <th className="py-2 pr-4 font-medium">Org</th>
                    <th className="py-2 pr-4 font-medium">Lado</th>
                    <th className="py-2 pr-4 font-medium">Status</th>
                    <th className="py-2 pr-4 font-medium">Divergências (aplic./detect.)</th>
                    <th className="py-2 pr-4 font-medium">Thumb</th>
                    <th className="py-2 pr-4 font-medium">Criado em</th>
                  </tr>
                </thead>
                <tbody>
                  {ultimas.map((r) => (
                    <tr key={r.id} className="border-b border-slate-800/50 text-slate-200">
                      <td className="py-2 pr-4 font-mono text-xs">#{r.id}</td>
                      <td className="py-2 pr-4">{r.org_name ?? `Org ${r.org_id ?? '—'}`}</td>
                      <td className="py-2 pr-4 text-xs">{r.lado_cliente}</td>
                      <td className="py-2 pr-4">
                        <Badge color={(STATUS_COLORS[r.status] ?? 'slate') as any} size="xs">
                          {r.status}
                        </Badge>
                      </td>
                      <td className="py-2 pr-4">
                        {r.divergencias_aplicadas} / {r.divergencias_detectadas}
                      </td>
                      <td className="py-2 pr-4">
                        {r.thumb === 'up' && '👍'}
                        {r.thumb === 'down' && '👎'}
                        {!r.thumb && '—'}
                      </td>
                      <td className="py-2 pr-4 text-xs">{fmtDataHora(r.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </Card>
    </div>
  );
}
